import React from 'react';
import { Pencil, Trash } from '@saleshandy/icons';
import { SkeletonLoading } from '@saleshandy/design-system';
import { SortOrder } from 'react-bootstrap-table-next';

import { DoNotCallList } from '../../../../../../../types/do-not-call-list';
import { DoNotCallListsSortBy } from '../../../../../../../enums/do-not-call-list';
import { Column } from '../../../../../../../../../shared/design-system/components/organisms/table/types';

import ListNameField from '../fields/list-name-field';
import TotalField from '../fields/total-field';
import ClientAssociatedField from '../fields/client-associated-field';
import AddedByField from '../fields/added-by-field';
import UpdatedAtField from '../fields/updated-at-field';
import { isAgency } from '../../../../../../../../../shared/utils/user-details';
import hasPermission from '../../../../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../../../../shared/utils/access-control/enums/permissions';

type GetDoNotCallListColumns = {
  onSort: (sortBy: DoNotCallListsSortBy, order: SortOrder) => void;
  onAction: (key: string, doNotCallList: DoNotCallList) => void;
  isCellClickable: boolean;
};

const actions = [
  {
    key: 'edit',
    iconElement: <Pencil />,
    displayName: 'Modify',
    position: 'out',
    permission: Permissions.DNC_WRITE,
  },
  {
    key: 'delete',
    iconElement: <Trash />,
    displayName: 'Delete',
    position: 'out',
    permission: Permissions.DNC_DELETE,
  },
];

export const getDoNotCallListColumns = ({
  onSort,
  onAction,
  isCellClickable,
}: GetDoNotCallListColumns): Column[] => {
  const cellClassName = isCellClickable ? 'cursor-pointer' : '';

  const isClientColVisible = hasPermission(Permissions.CLIENT_READ) && isAgency();

  const colWidth = {
    col1: isClientColVisible ? '30%' : '55%',
    col2: '10%',
    col3: '25%',
    col4: '10%',
    col5: isClientColVisible ? '25%' : '25%',
  };

  const getColWidth = (key: string) => ({
    minWidth: colWidth[key],
    width: colWidth[key],
  });

  return [
    {
      dataField: 'listName',
      text: 'List Name',
      headerAlign: 'start',
      cellClasses: `list-name-cell ${cellClassName}`,
      component: (_cell, row: DoNotCallList) => (
        <ListNameField doNotCallList={row} />
      ),
      headerStyle: () => getColWidth('col1'),
      headerLoadingSkeleton: <SkeletonLoading width={80} height={14} />,
      cellLoadingSkeleton: <SkeletonLoading width={180} height={14} />,
    },
    {
      dataField: 'totalCount',
      text: 'Total',
      headerAlign: 'start',
      cellClasses: `total-score-cell ${cellClassName}`,
      sort: true,
      onSort: (_field, order) => onSort(DoNotCallListsSortBy.Total, order),
      component: (_cell, row: DoNotCallList) => (
        <TotalField doNotCallList={row} />
      ),
      headerStyle: () => getColWidth('col2'),
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    isClientColVisible && {
      dataField: 'client',
      text: 'Client Associated',
      headerAlign: 'start',
      cellClasses: `client-associated-cell ${cellClassName}`,
      sort: true,
      onSort: (_field, order) =>
        onSort(DoNotCallListsSortBy.ClientAssociated, order),
      component: (_cell, row: DoNotCallList) => (
        <ClientAssociatedField doNotCallList={row} />
      ),
      headerStyle: () => getColWidth('col3'),
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'user',
      text: 'Added By',
      headerAlign: 'start',
      cellClasses: `added-by-cell ${cellClassName}`,
      component: (_cell, row: DoNotCallList) => (
        <AddedByField doNotCallList={row} />
      ),
      headerStyle: () => getColWidth('col4'),
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
    {
      dataField: 'updatedAt',
      text: 'Updated At',
      headerAlign: 'start',
      cellClasses: 'updated-at-cell',
      component: (_cell, row: DoNotCallList) => (
        <UpdatedAtField
          doNotCallList={row}
          actions={actions}
          onAction={onAction}
        />
      ),
      sort: true,
      onSort: (_field, order) => onSort(DoNotCallListsSortBy.UpdatedAt, order),
      isCellClickable: false,
      headerStyle: () => getColWidth('col5'),
      headerLoadingSkeleton: <SkeletonLoading width={62} height={14} />,
      cellLoadingSkeleton: (
        <SkeletonLoading width={32} height={24} borderRadius={20} />
      ),
    },
  ].filter((col) => col) as Column[];
};
